
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SeoMetadata from "@/components/common/SeoMetadata";
import UscoreAdvertisement from "@/components/home/UscoreAdvertisement";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import { 
  Scale, 
  Calculator, 
  AlertCircle, 
  Info, 
  FileText, 
  ArrowRight, 
  ExternalLink, 
  Home, 
  Shield, 
  HelpCircle,
  RefreshCcw
} from "lucide-react";

const UlosottolaskuriPage = () => {
  const isMobile = useIsMobile();
  
  // State for calculator
  const [salary, setSalary] = useState(2500);
  const [otherIncome, setOtherIncome] = useState(0);
  const [totalIncome, setTotalIncome] = useState(2500);
  const [result, setResult] = useState({
    ulosottoAmount: 833.33,
    remainingIncome: 1666.67,
    percentage: 33.33
  });
  
  // Handle income change
  const handleSalaryChange = (value: number[]) => {
    const newSalary = value[0];
    setSalary(newSalary);
    calculateUlosotto(newSalary, otherIncome);
  };
  
  const handleOtherIncomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setOtherIncome(value);
    calculateUlosotto(salary, value);
  };
  
  // Calculate the enforcement amount
  const calculateUlosotto = (salary: number, other: number) => {
    const total = salary + other;
    setTotalIncome(total);
    
    // 2025 protection portion (päivittäinen 32,88 € * 30 päivää)
    const protectionPortion = 986.40; 
    
    let ulosottoAmount = 0;
    let percentage = 0;
    
    if (total <= protectionPortion) {
      ulosottoAmount = 0;
      percentage = 0;
    } else if (total <= 2 * protectionPortion) {
      // 2/3 of the amount exceeding the protection portion
      ulosottoAmount = (total - protectionPortion) * (2/3);
      percentage = (ulosottoAmount / total) * 100;
    } else if (total <= 4 * protectionPortion) {
      // 1/3 of the protection portion + income exceeding 2x protection portion
      ulosottoAmount = protectionPortion * (1/3) + (total - 2 * protectionPortion);
      percentage = (ulosottoAmount / total) * 100;
    } else {
      // One-third of the amount up to four times the basic amount and four-fifths of any excess
      ulosottoAmount = protectionPortion * (1/3) + (2 * protectionPortion) + 
        (total - 4 * protectionPortion) * (4/5);
      percentage = (ulosottoAmount / total) * 100;
    }
    
    setResult({
      ulosottoAmount: Math.min(ulosottoAmount, total * 0.5), // Maximum is 50% of income
      remainingIncome: total - Math.min(ulosottoAmount, total * 0.5),
      percentage: Math.min(percentage, 50) // Maximum is 50%
    });
  };
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5
      }
    })
  };
  
  return (
    <>
      <SeoMetadata
        title="Ulosottolaskuri 2025 - Laske ulosoton määrä palkasta ja eläkkeestä"
        description="Ilmainen ulosottolaskuri 2025 - Laske ulosoton määrä palkasta tai eläkkeestä. Tietoa ulosotosta, velallisen oikeuksista ja velkaongelmien ratkaisuista."
        keywords={["ulosottolaskuri", "ulosotto", "palkan ulosmittaus", "velan ulosmittaus", "ulosottoperintä", "velallisen oikeudet"]}
        canonicalUrl="https://lainasampo.fi/lainat/ulosottolaskuri"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 py-6 px-4 md:py-12">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial="hidden"
            animate="visible"
            custom={0}
            variants={fadeIn}
            className="text-center md:text-left mb-8"
          >
            <h1 className="text-3xl md:text-4xl font-bold mb-3 text-blue-700 bg-gradient-to-r from-blue-700 to-blue-500 bg-clip-text text-transparent">
              Ulosottolaskuri
            </h1>
            <p className="text-gray-600 md:text-lg max-w-2xl mx-auto md:mx-0">
              Laske ulosoton määrä palkasta tai eläkkeestä ilmaisella ulosottolaskurilla. Saat arvion siitä, 
              kuinka paljon ulosmittaus voisi olla tuloistasi.
            </p>
          </motion.div>
          
          {/* Two-column layout for calculator and info */}
          <div className="flex flex-col lg:flex-row gap-8 mb-10">
            {/* Calculator Section - Narrower */}
            <motion.div 
              className="lg:w-[40%]"
              initial="hidden"
              animate="visible"
              custom={1}
              variants={fadeIn}
            >
              <Card className="shadow-lg border-blue-100 overflow-hidden rounded-xl">
                <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-500 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl font-bold">Ulosottolaskuri</CardTitle>
                      <CardDescription className="text-blue-100">
                        Laske arvio ulosoton määrästä tuloistasi
                      </CardDescription>
                    </div>
                    <Calculator className="h-7 w-7 text-white opacity-90" />
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="salary" className="block text-sm font-medium text-gray-700 mb-2">
                        Palkka tai eläke kuukaudessa (netto): <span className="font-semibold text-blue-700">{salary} €</span>
                      </label>
                      <Slider
                        id="salary"
                        min={0}
                        max={6000}
                        step={50}
                        value={[salary]}
                        onValueChange={handleSalaryChange}
                        className="py-4"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0 €</span>
                        <span>3000 €</span>
                        <span>6000 €</span>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="otherIncome" className="block text-sm font-medium text-gray-700 mb-2">
                        Muut tulot kuukaudessa (netto)
                      </label>
                      <Input
                        id="otherIncome"
                        type="number"
                        value={otherIncome}
                        onChange={handleOtherIncomeChange}
                        className="max-w-xs"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    
                    <div className="pt-4 border-t border-gray-200">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-blue-700 mb-3">Tulokset</h3>
                        
                        <div className="grid grid-cols-1 gap-3">
                          <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-100 hover:shadow-md transition-shadow">
                            <p className="text-sm text-gray-500">Ulosoton määrä</p>
                            <p className="text-2xl font-bold text-blue-700">{result.ulosottoAmount.toFixed(2)} €</p>
                            <p className="text-xs text-gray-500 mt-1">({result.percentage.toFixed(1)}% tuloista)</p>
                          </div>
                          
                          <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-100 hover:shadow-md transition-shadow">
                            <p className="text-sm text-gray-500">Jää käteen</p>
                            <p className="text-2xl font-bold text-green-600">{result.remainingIncome.toFixed(2)} €</p>
                            <p className="text-xs text-gray-500 mt-1">Ulosoton jälkeen</p>
                          </div>
                          
                          <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-100 hover:shadow-md transition-shadow">
                            <p className="text-sm text-gray-500">Kokonaistulot</p>
                            <p className="text-2xl font-bold text-gray-700">{totalIncome.toFixed(2)} €</p>
                            <p className="text-xs text-gray-500 mt-1">Ennen ulosottoa</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4 flex justify-center">
                        <div className="flex items-center text-yellow-600 bg-yellow-50 px-3 py-2 rounded text-sm">
                          <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                          <p>Laskelmaa ei voida pitää lupauksena tai takauksena ulosoton lopullisesta määrästä.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            
            {/* Information section beside calculator */}
            <motion.div 
              className="lg:w-[60%]"
              initial="hidden"
              animate="visible"
              custom={2}
              variants={fadeIn}
            >
              <Card className="shadow-lg border-blue-100 h-full rounded-xl">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-100">
                  <div className="flex items-center">
                    <RefreshCcw className="h-5 w-5 text-blue-600 mr-2" />
                    <div>
                      <h3 className="font-semibold text-lg text-blue-700">Ulosoton määrän perusteet</h3>
                      <CardDescription>Palkan tai eläkkeen ulosmittaus lasketaan seuraavan asteikon mukaan</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-blue-50">
                          <th className="p-2 text-left text-sm font-medium text-blue-700 border border-blue-100 rounded-tl-lg">Tulon määrä</th>
                          <th className="p-2 text-left text-sm font-medium text-blue-700 border border-blue-100 rounded-tr-lg">Ulosmittauksen määrä</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        <tr>
                          <td className="p-3 border border-blue-100">Alle suojaosuuden (986,40 €)</td>
                          <td className="p-3 border border-blue-100 font-medium">Ei ulosmittausta</td>
                        </tr>
                        <tr className="bg-blue-50/30">
                          <td className="p-3 border border-blue-100">Suojaosuus - 2 × suojaosuus</td>
                          <td className="p-3 border border-blue-100 font-medium">2/3 suojaosuuden ylittävästä osasta</td>
                        </tr>
                        <tr>
                          <td className="p-3 border border-blue-100">2 × suojaosuus - 4 × suojaosuus</td>
                          <td className="p-3 border border-blue-100 font-medium">1/3 suojaosuudesta + ylimenevä osa</td>
                        </tr>
                        <tr className="bg-blue-50/30">
                          <td className="p-3 border border-blue-100 rounded-bl-lg">Yli 4 × suojaosuus</td>
                          <td className="p-3 border border-blue-100 font-medium rounded-br-lg">1/3 suojaosuudesta + 2 × suojaosuus + 4/5 ylimenevästä osasta</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-6 space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-700 mb-2">Päivitetyt suojaosuudet 2025</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        Vuonna 2025 ulosotossa olevalle velalliselle jätettävä suojaosuus on <strong>32,88 euroa päivässä</strong> velallisen itsensä osalta ja <strong>9,61 euroa päivässä</strong> velallisen elatuksen varassa olevan henkilön osalta.
                      </p>
                      <Link to="https://oikeusministerio.fi/-/suojaosuuden-maaraan-maaraajoin-maksettavan-palkan-ulosmittauksessa-ja-velkajarjestelyssa-huomioon-otettaviin-valttamattomiin-elinkustannuksiin-korotus-vuoden-2025-alussa" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm flex items-center">
                        <ExternalLink size={14} className="mr-1" />
                        Lähde: Oikeusministeriö
                      </Link>
                    </div>
                    
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-medium text-green-700 mb-2">Velallisen oikeudet</h4>
                      <p className="text-sm text-gray-600">
                        Velallisella on oikeus ulosoton rajoittamiseen tai vapaakuukausiin tietyissä tilanteissa.
                        Ota yhteyttä ulosottoviranomaiseen keskustellaksesi tilanteestasi.
                      </p>
                    </div>
                    
                    <div className="p-4 bg-yellow-50 rounded-lg">
                      <h4 className="font-medium text-yellow-700 mb-2">Ulosoton kesto</h4>
                      <p className="text-sm text-gray-600">
                        Ulosotto jatkuu kunnes velka on maksettu kokonaan tai se vanhentuu. Useimmat ulosottoperusteet 
                        vanhenevat 15 tai 20 vuodessa, mutta esimerkiksi elatusapuvelat eivät vanhene koskaan.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
          
          {/* Resources section - 3 columns mobile responsive */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            <motion.div 
              className="col-span-1"
              initial="hidden"
              animate="visible"
              custom={3}
              variants={fadeIn}
            >
              <Card className="h-full shadow-md hover:shadow-lg transition-shadow border-blue-100 rounded-xl">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-100">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-blue-600" />
                    <h3 className="font-semibold">Ulosoton perusteet</h3>
                  </div>
                </CardHeader>
                <CardContent className="p-4 text-sm text-gray-600 space-y-3">
                  <p className="flex items-start">
                    <ExternalLink className="h-4 w-4 mr-1.5 mt-0.5 text-blue-500 flex-shrink-0" />
                    <Link to="https://oikeus.fi/ulosotto/fi/index.html" target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-600">
                      Oikeus.fi - Virallista tietoa ulosotosta
                    </Link>
                  </p>
                  
                  <p className="flex items-start">
                    <ExternalLink className="h-4 w-4 mr-1.5 mt-0.5 text-blue-500 flex-shrink-0" />
                    <Link to="https://www.vero.fi/henkiloasiakkaat/velvollisuudet/velat/ulosotto/" target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-600">
                      Vero.fi - Ulosotto
                    </Link>
                  </p>
                  
                  <p className="flex items-start">
                    <ExternalLink className="h-4 w-4 mr-1.5 mt-0.5 text-blue-500 flex-shrink-0" />
                    <Link to="https://www.takuusaatio.fi/palvelumme/velkalinja/" target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-600">
                      Takuusäätiön velkalinja
                    </Link>
                  </p>
                  
                  <div className="flex items-start mt-4 p-3 bg-blue-50 rounded-lg">
                    <Scale className="h-5 w-5 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-700">Lainsäädäntö</p>
                      <p>Ulosottoon liittyviä velvoitteita ja oikeuksia säätelee ulosottokaari (705/2007).</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            
            <motion.div 
              className="col-span-1"
              initial="hidden"
              animate="visible"
              custom={4}
              variants={fadeIn}
            >
              <Card className="h-full shadow-md hover:shadow-lg transition-shadow border-blue-100 rounded-xl">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-100">
                  <div className="flex items-center">
                    <ArrowRight className="h-5 w-5 mr-2 text-green-600" />
                    <h3 className="font-semibold">Ratkaisuja velkaongelmiin</h3>
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <Link to="/lainat/yhdistelylaina" className="flex items-center text-blue-600 hover:underline p-2 rounded-lg hover:bg-blue-50 transition-colors group">
                    <Home className="h-5 w-5 mr-2 flex-shrink-0 group-hover:text-blue-700 transition-colors" />
                    <div>
                      <p className="font-medium">Yhdistelylaina</p>
                      <p className="text-xs text-gray-600">Yhdistä kaikki lainasi yhdeksi edullisemmaksi lainaksi</p>
                    </div>
                  </Link>
                  
                  <Link to="/kilpailuta" className="flex items-center text-blue-600 hover:underline p-2 rounded-lg hover:bg-blue-50 transition-colors group">
                    <ArrowRight className="h-5 w-5 mr-2 flex-shrink-0 group-hover:text-blue-700 transition-colors" />
                    <div>
                      <p className="font-medium">Kilpailuta lainasi</p>
                      <p className="text-xs text-gray-600">Vertaile eri rahoitusyhtiöiden lainatarjouksia</p>
                    </div>
                  </Link>
                  
                  <div className="p-3 bg-green-50 rounded-lg mt-4">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium text-green-700 block mb-1">Tiesitkö?</span>
                      Yhdistämällä useita pienempiä lainoja yhdeksi isommaksi lainaksi voit säästää jopa satoja euroja kuukaudessa.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            
            <motion.div 
              className="col-span-1"
              initial="hidden"
              animate="visible"
              custom={5}
              variants={fadeIn}
            >
              <Card className="h-full shadow-md hover:shadow-lg transition-shadow border-blue-100 rounded-xl">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-100">
                  <div className="flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-blue-600" />
                    <h3 className="font-semibold">Lisätietoa ulosotosta</h3>
                  </div>
                </CardHeader>
                <CardContent className="p-4 text-sm space-y-3">
                  <Link to="/artikkelit/ulosoton-abc-kaikki-mita-sinun-tulee-tietaa-ulosotosta" className="flex items-center p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group">
                    <FileText className="h-5 w-5 mr-2 flex-shrink-0 text-blue-600 group-hover:text-blue-700" />
                    <div>
                      <p className="font-medium text-blue-700 group-hover:underline">Ulosoton ABC</p>
                      <p className="text-gray-600">Kattava perustietopaketti ulosotosta, sen vaiheista ja vaikutuksista</p>
                    </div>
                  </Link>
                  
                  <div className="p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                    <p className="font-medium text-gray-700">Mitä ulosotolla tarkoitetaan?</p>
                    <p className="text-gray-600 mt-1">Ulosotto on laiminlyötyjen maksuvelvoitteiden pakollista täytäntöönpanoa viranomaisten toimesta.</p>
                  </div>
                  
                  <div className="p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                    <p className="font-medium text-gray-700">Miten pääsen eroon ulosotosta?</p>
                    <p className="text-gray-600 mt-1">Maksamalla velat kokonaan tai neuvottelemalla maksujärjestelystä ulosottomiehen kanssa.</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
          
          {/* Call to Action */}
          <motion.div 
            initial="hidden"
            animate="visible"
            custom={6}
            variants={fadeIn}
            className="mb-10"
          >
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-xl p-6 md:p-8 text-white shadow-lg">
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-xl md:text-2xl font-semibold mb-4">Ongelmia lainojen kanssa?</h2>
                <p className="mb-6 text-blue-100 md:text-lg">
                  Yhdistelylaina voi auttaa sinua pienentämään kuukausittaisia lainanhoitokuluja ja välttämään ulosottoa. 
                  Vertaile eri lainantarjoajien yhdistelylainoja ilmaiseksi Lainasampo.fi:n kautta.
                </p>
                
                <Link to="/lainat/yhdistelylaina" className="inline-block bg-white text-blue-600 py-3 px-6 rounded-lg font-medium hover:bg-blue-50 transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-transform">
                  Tutustu yhdistelylainoihin <ArrowRight className="inline-block ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>
          </motion.div>
          
          {/* FAQ Section with accordions - mobile optimized */}
          <motion.div 
            initial="hidden"
            animate="visible"
            custom={7}
            variants={fadeIn}
            className="mb-10"
          >
            <Card className="shadow-lg border-blue-100 rounded-xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-100">
                <div className="flex items-center">
                  <HelpCircle className="h-5 w-5 mr-2 text-blue-600" />
                  <div>
                    <h3 className="font-semibold text-lg text-blue-700">Mistä saan apua ulosottotilanteeseen?</h3>
                    <CardDescription>Viranomaiset ja palvelut, jotka tarjoavat neuvontaa</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-blue-700 border-b border-blue-100 pb-2">Viranomaiset ja palvelut</h4>
                    
                    <Link to="https://oikeus.fi/ulosotto/fi/" target="_blank" rel="noopener noreferrer" className="flex items-center p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                      <div className="flex-grow">
                        <p className="font-medium text-blue-700">Ulosottoviranomaiset</p>
                        <p className="text-sm text-gray-600">Virallinen ulosottohallinto</p>
                      </div>
                      <ExternalLink className="h-4 w-4 flex-shrink-0 text-blue-500" />
                    </Link>
                    
                    <Link to="https://www.takuusaatio.fi" target="_blank" rel="noopener noreferrer" className="flex items-center p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                      <div className="flex-grow">
                        <p className="font-medium text-blue-700">Takuusäätiö</p>
                        <p className="text-sm text-gray-600">Maksuton neuvonta velallisille</p>
                      </div>
                      <ExternalLink className="h-4 w-4 flex-shrink-0 text-blue-500" />
                    </Link>
                    
                    <Link to="https://www.talousjavelkaneuvonta.fi" target="_blank" rel="noopener noreferrer" className="flex items-center p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                      <div className="flex-grow">
                        <p className="font-medium text-blue-700">Talous- ja velkaneuvonta</p>
                        <p className="text-sm text-gray-600">Maksutonta apua velkojen järjestelyyn</p>
                      </div>
                      <ExternalLink className="h-4 w-4 flex-shrink-0 text-blue-500" />
                    </Link>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="font-medium text-blue-700 border-b border-blue-100 pb-2">Hyödyllisiä artikkeleita</h4>
                    
                    <Link to="/artikkelit/ulosoton-abc-kaikki-mita-sinun-tulee-tietaa-ulosotosta" className="flex items-center p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group">
                      <FileText className="h-5 w-5 mr-2 text-blue-600 flex-shrink-0 group-hover:text-blue-700 transition-colors" />
                      <div>
                        <p className="font-medium group-hover:underline">Ulosoton ABC</p>
                        <p className="text-sm text-gray-600">Kattava opas ulosoton käytännöistä</p>
                      </div>
                    </Link>
                    
                    <Link to="/artikkelit/velallisen-oikeudet" className="flex items-center p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group">
                      <div>
                        <p className="font-medium group-hover:underline text-blue-700">Velallisen oikeudet</p>
                        <p className="text-sm text-gray-600">Mitä oikeuksia velallisella on ulosottotilanteessa?</p>
                      </div>
                    </Link>
                    
                    <Link to="/artikkelit/velkajarjestely" className="flex items-center p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group">
                      <div>
                        <p className="font-medium group-hover:underline text-blue-700">Velkajärjestely</p>
                        <p className="text-sm text-gray-600">Miten velkajärjestely toimii ja kuka voi sitä hakea?</p>
                      </div>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
        
        <div className="container mx-auto py-8 px-4">
          <UscoreAdvertisement />
        </div>
      </div>
    </>
  );
};

export default UlosottolaskuriPage;
